/**
 * Операции над привычками, выходящие за рамки CRUD: чек-ин и работа с историей.
 *
 * Отметка дня — самое частое действие в модуле, поэтому оно вынесено сюда и
 * ведёт себя одинаково на карточке, в тепловой карте и в детальной шторке.
 */

import { habitEntriesRepo } from '@/entities/habit-entry/model/repository';
import { db } from '@/shared/db/database';
import { DEFAULT_HABIT_TARGET_DAYS, HABIT_VALUE, type Habit, type HabitEntry } from '@/shared/db/schema';
import { getCurrentUserId } from '@/shared/db/session-context';
import { addDays, diffDays, formatDuration, type IsoDate, today } from '@/shared/lib/datetime';
import { describeWeekdays } from '@/shared/lib/weekdays';

import {
  type HabitTargetProgress,
  habitTargetProgress,
  isDayCompleted,
  isYes,
  scoreSpecOf,
} from './habit-score';

/* ──────────────────────────── Чтение истории ──────────────────────────── */

/**
 * Отметки всех привычек пользователя за период.
 *
 * Один индексный запрос по `[userId+date]` вместо запроса на каждую привычку:
 * на экране их может быть два десятка, и N запросов на каждый рендер заметно
 * подтормаживали бы список.
 */
export async function getEntriesInRange(from: IsoDate, to: IsoDate): Promise<HabitEntry[]> {
  const userId = getCurrentUserId();
  if (!userId) return [];

  const rows = await db.habitEntries
    .where('[userId+date]')
    .between([userId, from], [userId, to], true, true)
    .toArray();

  return rows.filter((entry) => entry.deletedAt === null);
}

/**
 * Все отметки пользователя без ограничения по датам.
 *
 * Нужны там, где важна вся жизнь привычки, а не последние три месяца: срок
 * закрепления может быть и годовым, и обрезанное окно занижало бы прогресс
 * цели. Записей ровно столько, сколько человек отметил дней, — это единицы
 * тысяч строк за годы, и выборка по индексу владельца ничего не стоит.
 */
export async function getAllEntries(): Promise<HabitEntry[]> {
  const userId = getCurrentUserId();
  if (!userId) return [];

  const rows = await db.habitEntries.where('userId').equals(userId).toArray();
  return rows.filter((entry) => entry.deletedAt === null);
}

/**
 * Продвижение привычек к своему сроку: `habitId → прогресс`.
 *
 * Считается по всей истории отметок, а не по окну тепловой карты: срок может
 * быть и годовым, и обрезанное окно занижало бы и карточку привычки, и
 * прогресс связанной цели. Обе картинки обязаны сходиться, поэтому источник у
 * них один.
 */
export function habitProgressByHabit(
  habits: readonly Habit[],
  entries: readonly HabitEntry[],
): Map<string, HabitTargetProgress> {
  const doneDays = new Map<string, number>();
  const firstDone = new Map<string, IsoDate>();
  const specs = new Map(habits.map((habit) => [habit.id, scoreSpecOf(habit)]));

  for (const entry of entries) {
    const spec = specs.get(entry.habitId);
    if (!spec || !isDayCompleted(spec, entry.value)) continue;

    doneDays.set(entry.habitId, (doneDays.get(entry.habitId) ?? 0) + 1);

    const known = firstDone.get(entry.habitId);
    if (known === undefined || entry.date < known) firstDone.set(entry.habitId, entry.date);
  }

  return new Map(
    habits.map((habit) => [
      habit.id,
      habitTargetProgress(
        doneDays.get(habit.id) ?? 0,
        habitTargetDays(habit),
        firstDone.get(habit.id) ?? null,
      ),
    ]),
  );
}

/**
 * Срок привычки в пригодном для расчёта виде.
 *
 * У привычек, заведённых до появления этого поля, его может не оказаться:
 * миграция проставляет значение при открытии базы, но запись могла прийти и
 * другим путём — из старой вкладки или будущей синхронизации. Без проверки
 * `Math.round(undefined)` даёт `NaN`, и дальше рассыпается всё: окно карты
 * превращается в некорректную дату, доля выполнения — в `NaN`, а привычка
 * просто перестаёт показываться правильно.
 */
export function habitTargetDays(habit: Pick<Habit, 'targetDays'>): number {
  const value = Math.round(habit.targetDays);
  return Number.isFinite(value) && value > 0 ? value : DEFAULT_HABIT_TARGET_DAYS;
}

/**
 * Окно тепловой карты привычки.
 *
 * Начинается с первого выполненного дня — с начала попытки, а не с даты
 * создания привычки. Разница решающая: привычку заводят заранее и начинают
 * когда получится, и если отсчитывать от создания, первая отметка попадает в
 * конец длинного ряда пустых клеток. Человек читает это как ошибку: «отметил, а
 * закрасилось где-то в углу». От начала попытки цвет растёт слева направо, как
 * строка текста.
 *
 * Пока не отмечено ни дня, попытка считается начинающейся сегодня — карта
 * чистая и ждёт первой клетки. Дни, прошедшие между созданием и первой
 * отметкой, в карту не попадают: они не были ни выполнены, ни провалены,
 * человек просто ещё не начал, и показывать их как пропуски было бы упрёком на
 * пустом месте.
 *
 * Длина — назначенный срок, но не короче прожитого с начала попытки. Пока
 * привычка моложе срока, клеток ровно столько, сколько дней назначено, и
 * будущие стоят пустыми: объём обязательства и остаток видны сразу. Когда
 * привычка переживает свой срок, карта растёт вместе с ней — обрезать историю
 * ради красивого числа клеток значило бы скрыть её вторую половину.
 */
export function habitMapRange(
  habit: Pick<Habit, 'startDate' | 'targetDays'>,
  firstDoneDate: IsoDate | null,
  reference: IsoDate = today(),
): { from: IsoDate; to: IsoDate } {
  const target = habitTargetDays(habit);

  // Привычка, запланированная на будущее, отсчитывается от своего старта.
  const notStarted = habit.startDate > reference ? habit.startDate : reference;
  const from = firstDoneDate ?? notStarted;

  const lived = Math.max(1, diffDays(from, reference) + 1);
  return { from, to: addDays(from, Math.max(target, lived) - 1) };
}

/** Отметки, разложенные по привычкам: `habitId → (дата → значение)`. */
export function groupEntriesByHabit(
  entries: readonly HabitEntry[],
): Map<string, Map<string, number>> {
  const grouped = new Map<string, Map<string, number>>();

  for (const entry of entries) {
    let byDate = grouped.get(entry.habitId);
    if (!byDate) {
      byDate = new Map<string, number>();
      grouped.set(entry.habitId, byDate);
    }
    byDate.set(entry.date, entry.value);
  }

  return grouped;
}

/* ───────────────────────────────  Чек-ин  ─────────────────────────────── */

/**
 * Записывает значение дня.
 *
 * Отметки не удаляются: «снять галочку» — это запись `UNKNOWN`, а не soft-delete.
 * Причина техническая: составной ключ `[habitId+date]` уникален, а мягко
 * удалённая запись продолжает его занимать, и повторная отметка того же дня
 * упала бы на ограничении уникальности.
 */
export async function setHabitValue(habitId: string, date: IsoDate, value: number): Promise<void> {
  // Чтение и запись — одной транзакцией. Без неё два быстрых нажатия по
  // чекбоксу оба видят «отметки нет» и оба идут создавать её, а составной
  // ключ уникален — второе нажатие падало с ошибкой ограничения в самом
  // частом действии приложения.
  await db.transaction('rw', db.habitEntries, db.outbox, async () => {
    const existing = await db.habitEntries.where('[habitId+date]').equals([habitId, date]).first();

    if (existing) {
      if (existing.deletedAt !== null) await habitEntriesRepo.restore(existing.id);
      await habitEntriesRepo.update(existing.id, { value });
      return;
    }

    await habitEntriesRepo.create({ habitId, date, value, note: '' });
  });
}

/**
 * Привычка в работе — не убранная в архив.
 *
 * Архивная привычка исчезает из трекера, но в прогрессе цели продолжала сидеть
 * в знаменателе с долей меньше единицы: цель не доходила до 100%, автоотметка
 * «достигнута» молча не срабатывала, а виновника на экране уже не было.
 */
export function isHabitActive(habit: Habit): boolean {
  return habit.archivedAt === null && habit.deletedAt === null;
}

/** Текущее значение дня. `UNKNOWN`, если отметки ещё нет. */
export function valueOf(entries: ReadonlyMap<string, number> | undefined, date: IsoDate): number {
  return entries?.get(date) ?? HABIT_VALUE.UNKNOWN;
}

/**
 * Чек-ин в один тап: «не отмечено / нет» → выполнено, выполнено → снять отметку.
 * Возвращает `true`, если день только что закрыт, — по этому признаку экран
 * решает, показывать ли поздравление.
 */
export async function toggleHabitDone(
  habit: Habit,
  date: IsoDate,
  current: number,
): Promise<boolean> {
  const willComplete = !isHabitDayDone(habit, current);

  if (habit.type === 'binary') {
    await setHabitValue(
      habit.id,
      date,
      willComplete ? HABIT_VALUE.YES_MANUAL : HABIT_VALUE.UNKNOWN,
    );
  } else {
    // Для счётчика и таймера «выполнено» — это ровно дневная норма: пользователь
    // подтверждает результат целиком, а не набирает его по шагу.
    await setHabitValue(habit.id, date, willComplete ? habit.targetValue : HABIT_VALUE.UNKNOWN);
  }

  return willComplete;
}

/** Шаг счётчика: одна единица для numeric, пять минут для таймера. */
export function habitStep(habit: Habit): number {
  return habit.type === 'duration' ? 5 * 60 : 1000;
}

/**
 * Прибавляет или убавляет значение счётчика.
 * Возвращает `true`, если после изменения дневная норма достигнута.
 */
export async function addHabitProgress(
  habit: Habit,
  date: IsoDate,
  current: number,
  delta: number,
): Promise<boolean> {
  // Сентинелы «не отмечено» и «пропущено» при первом нажатии считаются нулём.
  const base = current === HABIT_VALUE.UNKNOWN || current === HABIT_VALUE.SKIP ? 0 : current;
  const next = Math.max(0, base + delta);

  await setHabitValue(habit.id, date, next);
  return isHabitDayDone(habit, next);
}

/** Отмечает день как осознанно пропущенный — он выпадает из расчёта силы. */
export async function skipHabitDay(habitId: string, date: IsoDate): Promise<void> {
  await setHabitValue(habitId, date, HABIT_VALUE.SKIP);
}

/** Снимает любую отметку дня. */
export async function clearHabitDay(habitId: string, date: IsoDate): Promise<void> {
  await setHabitValue(habitId, date, HABIT_VALUE.UNKNOWN);
}

/** Закрыт ли день по дневной норме привычки. */
export function isHabitDayDone(habit: Habit, value: number): boolean {
  if (value === HABIT_VALUE.UNKNOWN || value === HABIT_VALUE.SKIP) return false;
  if (habit.type === 'binary') return isYes(value);
  if (habit.targetValue <= 0) return value > 0;
  return habit.direction === 'atMost' ? value <= habit.targetValue : value >= habit.targetValue;
}

/* ───────────────────────────  Форматирование  ─────────────────────────── */

/** Значение дня для показа человеку: «5 из 8 стак.», «20 мин». */
export function formatHabitValue(habit: Habit, value: number): string {
  if (value === HABIT_VALUE.UNKNOWN) return '—';
  if (value === HABIT_VALUE.SKIP) return 'Пропуск';

  if (habit.type === 'binary') return isYes(value) ? 'Выполнено' : 'Не выполнено';
  if (habit.type === 'duration') return formatDuration(Math.round(value / 60));

  const amount = value / 1000;
  const rounded = Number.isInteger(amount) ? String(amount) : amount.toFixed(1);
  return habit.unit ? `${rounded} ${habit.unit}` : rounded;
}

/** Дневная норма словами: «8 стак.», «20 мин», для binary — пустая строка. */
export function formatHabitTarget(habit: Habit): string {
  if (habit.type === 'binary') return '';
  if (habit.type === 'duration') return formatDuration(Math.round(habit.targetValue / 60));

  const amount = habit.targetValue / 1000;
  const rounded = Number.isInteger(amount) ? String(amount) : amount.toFixed(1);
  return habit.unit ? `${rounded} ${habit.unit}` : rounded;
}

/** Человеческое описание расписания — строка под названием привычки. */
export function describeSchedule(habit: Habit): string {
  switch (habit.scheduleType) {
    case 'daily':
      return 'Каждый день';
    case 'weekdays':
      // «По будням» вместо «Пн, Вт, Ср, Чт, Пт»: у привычек коротких
      // формулировок не было, хотя у задач и режима дня они есть.
      return habit.weekdaysMask === 0 ? 'Дни не выбраны' : describeWeekdays(habit.weekdaysMask);
    case 'timesPerWeek':
      return `${habit.freqNumerator} раз за ${habit.freqDenominator} дн.`;
    case 'everyNDays':
      return habit.intervalDays === 1 ? 'Каждый день' : `Каждые ${habit.intervalDays} дн.`;
  }
}
